import { createContext, useContext, useReducer, useEffect } from 'react'
import { useOfflineTestProgress, useOffline, useNetworkError, useApiCache } from '../hooks/useOffline'
import { getRandomQuestions } from '../data/questions'
import { auth } from '../lib/firebase'

// API calls now use relative URLs since backend is on same domain via Pages Functions
// For local development, you can proxy API calls in vite.config.js
const API_BASE_URL = '' // Relative URLs: /api/...

/**
 * Test Context for managing test state across components
 * Handles test mode selection, progress, and results
 */

const TestContext = createContext()

// Test state reducer
const testReducer = (state, action) => {
  switch (action.type) {
    case 'SET_MODE':
      return {
        ...state,
        mode: action.payload,
        status: 'ready'
      }
    
    case 'START_TEST':
      return {
        ...state,
        status: 'in_progress',
        startTime: Date.now(),
        currentQuestionIndex: 0,
        answers: {},
        timeLeft: action.payload.timeLimit,
        questionTimes: {},
        questionStartTime: Date.now()
      }
    
    case 'ANSWER_QUESTION':
      const currentTime = Date.now();
      const timeSpent = currentTime - (state.questionStartTime || currentTime);

      return {
        ...state,
        answers: {
          ...state.answers,
          [action.payload.questionId]: action.payload.answer
        },
        questionTimes: {
          ...state.questionTimes,
          [action.payload.questionId]: timeSpent
        }
      }
    
    case 'NEXT_QUESTION':
      return {
        ...state,
        currentQuestionIndex: Math.min(
          state.currentQuestionIndex + 1,
          (state.questions?.length || 1) - 1
        ),
        questionStartTime: Date.now() // Reset timer for new question
      }
    
    case 'PREVIOUS_QUESTION':
      return {
        ...state,
        currentQuestionIndex: Math.max(state.currentQuestionIndex - 1, 0),
        questionStartTime: Date.now() // Reset timer for new question
      }

    case 'JUMP_TO_QUESTION':
      return {
        ...state,
        currentQuestionIndex: action.payload,
        questionStartTime: Date.now() // Reset timer for new question
      }
    
    case 'UPDATE_TIME':
      return {
        ...state,
        timeLeft: Math.max(0, state.timeLeft - 1)
      }
    
    case 'SUBMIT_TEST':
      return {
        ...state,
        status: 'completed',
        endTime: Date.now(),
        results: action.payload
      }
    
    case 'RESET_TEST':
      return {
        ...initialState,
        status: 'mode_selection'
      }
    
    case 'SET_QUESTIONS':
      return {
        ...state,
        questions: action.payload
      }
    
    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload
      }
    
    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload,
        isLoading: false
      }
    
    default:
      return state
  }
}

// Initial state
const initialState = {
  mode: null, // 'free' | 'comprehensive'
  status: 'mode_selection', // 'mode_selection' | 'ready' | 'in_progress' | 'completed'
  questions: [],
  currentQuestionIndex: 0,
  answers: {},
  timeLeft: 0,
  startTime: null,
  endTime: null,
  results: null,
  isLoading: false,
  error: null,
  questionTimes: {}, // Track time spent on each question
  questionStartTime: null // Track when current question started
}

// Test Provider component
export const TestProvider = ({ children }) => {
  const [state, dispatch] = useReducer(testReducer, initialState)
  
  // Offline capabilities
  const { isOnline, wasOffline } = useOffline()
  const { 
    testProgress, 
    offlineAnswers, 
    offlineQuestions,
    saveTestProgress, 
    saveAnswer, 
    saveQuestions,
    clearOfflineData,
    hasOfflineData 
  } = useOfflineTestProgress()
  const { networkError, retryRequest, clearNetworkError } = useNetworkError()
  const { getCachedData, setCachedData } = useApiCache('questions')

  // Timer effect with offline support
  useEffect(() => {
    let timer
    if (state.status === 'in_progress' && state.timeLeft > 0) {
      timer = setInterval(() => {
        dispatch({ type: 'UPDATE_TIME' })
        // Save progress periodically when offline
        if (!isOnline) {
          saveTestProgress({
            mode: state.mode,
            currentQuestionIndex: state.currentQuestionIndex,
            timeLeft: state.timeLeft - 1,
            answers: state.answers
          })
        }
      }, 1000)
    } else if (state.timeLeft === 0 && state.status === 'in_progress') {
      // Auto-submit when time runs out
      handleSubmitTest()
    }

    return () => {
      if (timer) clearInterval(timer)
    }
  }, [state.status, state.timeLeft, isOnline, saveTestProgress, state.mode, state.currentQuestionIndex, state.answers])

  // Restore offline progress when coming back online
  useEffect(() => {
    if (isOnline && wasOffline && hasOfflineData && testProgress) {
      // Restore test state from offline storage
      dispatch({ type: 'SET_MODE', payload: testProgress.mode })
      dispatch({ type: 'JUMP_TO_QUESTION', payload: testProgress.currentQuestionIndex })
      
      // Restore answers
      Object.entries(offlineAnswers).forEach(([questionId, answerData]) => {
        dispatch({ 
          type: 'ANSWER_QUESTION', 
          payload: { questionId, answer: answerData.answer }
        })
      })
      
      // Restore questions if available
      if (offlineQuestions.length > 0) {
        dispatch({ type: 'SET_QUESTIONS', payload: offlineQuestions })
      }
      
      dispatch({ type: 'START_TEST', payload: { timeLimit: testProgress.timeLeft } })
    }
  }, [isOnline, wasOffline, hasOfflineData, testProgress, offlineAnswers, offlineQuestions])

  // Sync offline data when coming back online
  useEffect(() => {
    const handleSync = async () => {
      if (isOnline && hasOfflineData) {
        try {
          // Sync offline answers to server
          await syncOfflineData()
          clearOfflineData()
        } catch (error) {
          console.error('Failed to sync offline data:', error)
        }
      }
    }

    window.addEventListener('sync-offline-data', handleSync)
    return () => window.removeEventListener('sync-offline-data', handleSync)
  }, [isOnline, hasOfflineData, clearOfflineData])

  // Action creators
  const selectMode = (mode) => {
    // Clear any previous test data when selecting a new mode
    clearOfflineData()
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('api_cache_') || key.startsWith('offline_') || key.startsWith('test_')) {
        localStorage.removeItem(key)
      }
    })
    console.log('Selected mode:', mode, '- cleared all caches')
    dispatch({ type: 'SET_MODE', payload: mode })
  }

  const startTest = async (mode) => {
    dispatch({ type: 'SET_LOADING', payload: true })
    
    try {
      let questions = []
      
      if (isOnline) {
        // Try to fetch from API when online
        try {
          const questionCount = mode === 'comprehensive' ? 25 : 10
          const timestamp = Date.now() // Add timestamp to prevent caching
          const response = await retryRequest(async () => {
            const res = await fetch(`${API_BASE_URL}/api/questions/random?count=${questionCount}&t=${timestamp}`)
            if (!res.ok) throw new Error('Failed to fetch questions')
            return res
          })

          const apiResponse = await response.json()
          questions = apiResponse.data?.questions || apiResponse
          console.log('Fetched questions from API:', questions.slice(0, 3).map(q => q.id))
          
          // Save questions for offline use only, don't cache for reuse
          saveQuestions(questions)
        } catch (error) {
          console.log('API not available, using local questions:', error.message)
          // Use local sample questions as fallback (always randomized)
          const questionCount = mode === 'comprehensive' ? 25 : 10
          questions = getRandomQuestions(questionCount)
          saveQuestions(questions)
        }
      } else {
        // Use offline questions or generate new randomized questions when offline
        if (offlineQuestions.length > 0) {
          questions = offlineQuestions
        } else {
          // Use local sample questions when offline (always randomized)
          const questionCount = mode === 'comprehensive' ? 25 : 10
          questions = getRandomQuestions(questionCount)
          saveQuestions(questions)
        }
      }
      
      dispatch({ type: 'SET_QUESTIONS', payload: questions })
      dispatch({ type: 'START_TEST', payload: {
        timeLimit: mode === 'comprehensive' ? 1500 : 600 // 25 min vs 10 min
      }})
      dispatch({ type: 'SET_LOADING', payload: false })
      
      // Save initial progress for offline recovery
      saveTestProgress({
        mode,
        currentQuestionIndex: 0,
        timeLeft: mode === 'comprehensive' ? 1500 : 600,
        answers: {}
      })

    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message })
    }
  }

  const answerQuestion = (questionId, answer) => {
    console.log(`Recording answer: Question ${questionId} = ${answer}`)
    dispatch({ type: 'ANSWER_QUESTION', payload: { questionId, answer } })

    // Save answer for offline recovery
    saveAnswer(questionId, answer)
    
    // Update progress
    saveTestProgress({
      mode: state.mode,
      currentQuestionIndex: state.currentQuestionIndex,
      timeLeft: state.timeLeft,
      answers: { ...state.answers, [questionId]: answer }
    })
  }

  const nextQuestion = () => {
    dispatch({ type: 'NEXT_QUESTION' })
  }

  const previousQuestion = () => {
    dispatch({ type: 'PREVIOUS_QUESTION' })
  }

  const jumpToQuestion = (index) => {
    dispatch({ type: 'JUMP_TO_QUESTION', payload: index })
  }

  const handleSubmitTest = async () => {
    dispatch({ type: 'SET_LOADING', payload: true })

    try {
      // Calculate results locally first
      const results = calculateResults(state.questions, state.answers, state.mode)

      // Get or create session ID for tracking anonymous results
      let sessionId = localStorage.getItem('iqtest_session_id')
      if (!sessionId) {
        sessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9)
        localStorage.setItem('iqtest_session_id', sessionId)
      }

      // Get user ID if logged in from Firebase auth (not localStorage)
      const userId = auth.currentUser?.uid || null
      const userEmail = auth.currentUser?.email || null

      // Get display name with multiple fallbacks
      let displayName = null
      if (auth.currentUser) {
        displayName = auth.currentUser.displayName ||
                     auth.currentUser.email?.split('@')[0] ||
                     'User'
      }

      console.log('=== SUBMITTING TEST ===')
      console.log('Firebase UID:', auth.currentUser?.uid)
      console.log('Email:', auth.currentUser?.email)
      console.log('Display Name:', displayName)
      console.log('Raw Firebase displayName:', auth.currentUser?.displayName)
      console.log('======================')

      if (isOnline) {
        // Try to submit to API when online
        try {
          const payload = {
            mode: state.mode,
            questions: state.questions,
            answers: state.answers,
            timeSpent: Date.now() - state.startTime,
            questionTimes: state.questionTimes || {},
            sessionId: sessionId,
            userId: userId,
            userEmail: userEmail,
            displayName: displayName
          }

          console.log('Submitting test with payload:')
          console.log('Mode:', payload.mode)
          console.log('Questions count:', payload.questions.length)
          console.log('Answers:', payload.answers)
          console.log('SessionId:', sessionId)
          console.log('UserId:', userId)
          console.log('Sample question:', payload.questions[0])

          const response = await retryRequest(async () => {
            return fetch(`${API_BASE_URL}/api/tests/submit`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(payload)
            })
          })

          if (response.ok) {
            const apiResponse = await response.json()
            const serverResults = apiResponse.data || apiResponse

            console.log('=== SERVER RESPONSE ===')
            console.log('Full server response:', serverResults)
            console.log('Score:', serverResults.score)
            console.log('Performance:', serverResults.performance)
            console.log('======================')

            // Store result ID and session ID for later linking
            if (serverResults.resultId) {
              localStorage.setItem('last_result_id', serverResults.resultId)
            }
            if (serverResults.sessionId) {
              localStorage.setItem('iqtest_session_id', serverResults.sessionId)
            }

            // Transform server response to match frontend structure
            const normalizedResults = {
              iqScore: serverResults.score?.iq || serverResults.iqScore || 0,
              correctAnswers: serverResults.performance?.correctAnswers || serverResults.correctAnswers || 0,
              totalQuestions: serverResults.performance?.totalQuestions || serverResults.totalQuestions || state.questions.length,
              percentageCorrect: serverResults.performance?.accuracy || serverResults.percentageCorrect || 0,
              percentile: serverResults.score?.percentile || serverResults.percentile || 0,
              classification: serverResults.score?.classification || serverResults.classification || 'Unknown',
              timeCompleted: serverResults.performance?.timeSpent || serverResults.timeCompleted || (Date.now() - state.startTime),
              mode: serverResults.testType || state.mode,
              resultId: serverResults.resultId,
              sessionId: serverResults.sessionId,
              // Use empty/default values for detailed analytics since server doesn't provide them
              categoryBreakdown: {},
              timingAnalysis: { averageTime: Math.round((Date.now() - state.startTime) / state.questions.length) },
              difficultyAnalysis: {}
            }

            console.log('=== NORMALIZED RESULTS ===')
            console.log('Normalized results:', normalizedResults)
            console.log('correctAnswers:', normalizedResults.correctAnswers)
            console.log('iqScore:', normalizedResults.iqScore)
            console.log('==========================')

            dispatch({ type: 'SUBMIT_TEST', payload: normalizedResults })
            // Clear offline data after successful submission
            clearOfflineData()
          } else {
            console.log('Server response not OK, using local results')
            dispatch({ type: 'SUBMIT_TEST', payload: results })
          }
        } catch (error) {
          // Use local results if API fails
          dispatch({ type: 'SUBMIT_TEST', payload: results })
        }
      } else {
        // Use local calculation when offline
        dispatch({ type: 'SUBMIT_TEST', payload: results })

        // Save for later sync when online
        saveTestProgress({
          mode: state.mode,
          questions: state.questions,
          answers: state.answers,
          results,
          completed: true,
          timeSpent: Date.now() - state.startTime,
          sessionId: sessionId
        })
      }

      dispatch({ type: 'SET_LOADING', payload: false })
    } catch (error) {
      // Fallback to local calculation
      const results = calculateResults(state.questions, state.answers, state.mode)
      dispatch({ type: 'SUBMIT_TEST', payload: results })
      dispatch({ type: 'SET_LOADING', payload: false })
    }
  }

  const resetTest = () => {
    dispatch({ type: 'RESET_TEST' })
    clearOfflineData()
    clearNetworkError()
    // Clear ALL cached data to ensure fresh randomization
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('api_cache_') || key.startsWith('offline_') || key.startsWith('test_')) {
        localStorage.removeItem(key)
      }
    })
    console.log('Reset test - cleared all caches')
  }

  // Sync offline data to server
  const syncOfflineData = async () => {
    if (!hasOfflineData || !isOnline) return

    try {
      if (testProgress && testProgress.completed) {
        // Sync completed test results
        await fetch(`${API_BASE_URL}/api/tests/submit`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            mode: testProgress.mode,
            questions: testProgress.questions.map(q => q.id),
            answers: testProgress.answers,
            timeSpent: testProgress.timeSpent,
            offlineCompleted: true
          })
        })
      }
    } catch (error) {
      console.error('Failed to sync offline data:', error)
      throw error
    }
  }

  // Calculate results locally
  const calculateResults = (questions, answers, mode) => {
    let correctAnswers = 0
    const categoryBreakdown = {}

    questions.forEach(question => {
      const userAnswerIndex = answers[question.id]
      const correctAnswerText = question.answer
      const userAnswerText = userAnswerIndex !== undefined ? question.options[userAnswerIndex] : null
      const isCorrect = userAnswerText === correctAnswerText

      console.log(`Local calc - Question ${question.id}: User "${userAnswerText}" vs Correct "${correctAnswerText}" = ${isCorrect}`)

      if (isCorrect) correctAnswers++
      
      if (!categoryBreakdown[question.category]) {
        categoryBreakdown[question.category] = { correct: 0, total: 0 }
      }
      categoryBreakdown[question.category].total++
      if (isCorrect) categoryBreakdown[question.category].correct++
    })
    
    // Convert to percentages
    Object.keys(categoryBreakdown).forEach(category => {
      const { correct, total } = categoryBreakdown[category]
      categoryBreakdown[category] = Math.round((correct / total) * 100)
    })
    
    const accuracy = (correctAnswers / questions.length)

    // Enhanced scoring with deviation IQ methodology
    const iqScore = calculateDeviationIQ(accuracy, questions, state.questionTimes || {});
    const percentile = calculateAccuratePercentile(iqScore);
    const classification = getEnhancedClassification(iqScore);

    return {
      iqScore,
      correctAnswers,
      totalQuestions: questions.length,
      percentageCorrect: Math.round(accuracy * 100),
      percentile,
      categoryBreakdown,
      timeCompleted: Date.now() - state.startTime,
      classification,
      mode,
      timingAnalysis: calculateTimingAnalysis(questions, state.questionTimes || {}),
      difficultyAnalysis: calculateDifficultyAnalysis(questions, state.answers || {})
    }
  }

  // Enhanced deviation IQ calculation
  const calculateDeviationIQ = (accuracy, questions, questionTimes) => {
    let weightedScore = 0;
    let totalPossibleWeight = 0;
    let timeBonus = 0;

    // Calculate weighted accuracy with difficulty and time bonuses
    questions.forEach((q, index) => {
      const weight = getDifficultyWeight(q.difficulty || 'medium');

      // FIX: Properly check if answer is correct, not just if it exists
      const userAnswerIndex = state.answers[q.id];
      const correctAnswerText = q.answer;
      const userAnswerText = userAnswerIndex !== undefined ? q.options[userAnswerIndex] : null;
      const isCorrect = userAnswerText === correctAnswerText;

      totalPossibleWeight += weight;

      if (isCorrect) {
        weightedScore += weight;

        // Add time bonus for quick answers
        const timeSpent = questionTimes[q.id] || 60000; // Default 60s if not tracked
        const expectedTime = getExpectedTime(q.difficulty || 'medium') * 1000;

        if (timeSpent < expectedTime) {
          const timeEfficiency = expectedTime / timeSpent;
          const bonus = Math.min(0.3, (timeEfficiency - 1) * 0.1) * weight;
          timeBonus += bonus;
        }
      }
    });

    // Calculate final accuracy with bonuses
    const finalAccuracy = totalPossibleWeight > 0 ?
      (weightedScore + timeBonus) / totalPossibleWeight : accuracy;

    // Convert to deviation IQ (mean=100, sd=15)
    const populationMean = 0.70;
    const populationStd = 0.15;
    const zScore = (finalAccuracy - populationMean) / populationStd;
    const iq = 100 + (zScore * 15);

    // Apply reasonable bounds
    return Math.max(40, Math.min(200, Math.round(iq)));
  };

  const getDifficultyWeight = (difficulty) => {
    const weights = { easy: 1.0, medium: 1.3, hard: 1.7, expert: 2.2 };
    return weights[difficulty] || 1.0;
  };

  const getExpectedTime = (difficulty) => {
    const times = { easy: 30, medium: 60, hard: 90, expert: 120 };
    return times[difficulty] || 60;
  };

  const calculateAccuratePercentile = (iqScore) => {
    // Calculate z-score and convert to percentile using normal distribution
    const zScore = (iqScore - 100) / 15;
    const percentile = normalCDF(zScore) * 100;
    return Math.max(0.1, Math.min(99.9, Math.round(percentile * 10) / 10));
  };

  // Normal CDF approximation
  const normalCDF = (x) => {
    const a1 = 0.254829592, a2 = -0.284496736, a3 = 1.421413741;
    const a4 = -1.453152027, a5 = 1.061405429, p = 0.3275911;
    const sign = x < 0 ? -1 : 1;
    x = Math.abs(x) / Math.sqrt(2.0);
    const t = 1.0 / (1.0 + p * x);
    const y = 1.0 - (((((a5 * t + a4) * t) + a3) * t + a2) * t + a1) * t * Math.exp(-x * x);
    return 0.5 * (1.0 + sign * y);
  };

  const getEnhancedClassification = (score) => {
    if (score >= 180) return 'Exceptionally Gifted';
    if (score >= 160) return 'Extremely Gifted';
    if (score >= 145) return 'Highly Gifted';
    if (score >= 130) return 'Moderately Gifted';
    if (score >= 120) return 'Superior';
    if (score >= 110) return 'High Average';
    if (score >= 90) return 'Average';
    if (score >= 80) return 'Low Average';
    if (score >= 70) return 'Borderline';
    if (score >= 55) return 'Below Average';
    return 'Significantly Below Average';
  };

  const calculateTimingAnalysis = (questions, questionTimes) => {
    let totalTime = 0;
    let fastAnswers = 0;
    let slowAnswers = 0;

    questions.forEach(q => {
      const timeSpent = questionTimes[q.id] || 60000;
      const expectedTime = getExpectedTime(q.difficulty || 'medium') * 1000;

      totalTime += timeSpent;
      if (timeSpent < expectedTime * 0.8) fastAnswers++;
      else if (timeSpent > expectedTime * 1.5) slowAnswers++;
    });

    return {
      averageTime: Math.round(totalTime / questions.length / 1000),
      fastAnswers,
      slowAnswers,
      consistency: 1 - Math.abs(fastAnswers - slowAnswers) / questions.length
    };
  };

  const calculateDifficultyAnalysis = (questions, answers) => {
    const analysis = { easy: 0, medium: 0, hard: 0, expert: 0 };
    const totals = { easy: 0, medium: 0, hard: 0, expert: 0 };

    questions.forEach(q => {
      const difficulty = q.difficulty || 'medium';
      totals[difficulty]++;
      if (answers[q.id] !== undefined) {
        analysis[difficulty]++;
      }
    });

    Object.keys(analysis).forEach(level => {
      if (totals[level] > 0) {
        analysis[level] = Math.round((analysis[level] / totals[level]) * 100);
      }
    });

    return analysis;
  };

  // Legacy classification for compatibility
  const getClassification = getEnhancedClassification;

  const value = {
    ...state,
    isOnline,
    networkError,
    hasOfflineData,
    selectMode,
    startTest,
    answerQuestion,
    nextQuestion,
    previousQuestion,
    jumpToQuestion,
    submitTest: handleSubmitTest,
    resetTest,
    syncOfflineData,
    clearNetworkError
  }

  return (
    <TestContext.Provider value={value}>
      {children}
    </TestContext.Provider>
  )
}

// Custom hook to use test context
export const useTest = () => {
  const context = useContext(TestContext)
  if (!context) {
    throw new Error('useTest must be used within a TestProvider')
  }
  return context
}

export default TestContext